import { api } from './api'
import { ASSISTANT_CHAT_ID_KEY } from '@/components/assistant/assistant-roles'

export interface AuthUser {
  id: string
  email: string
  firstName: string
  lastName: string
  phone: string | null
  bio: string | null
  linkedinUrl: string | null
  instagramUrl: string | null
  facebookUrl: string | null
  role: string
  tenantId: string
  branchId: string | null
  avatarUrl: string | null
  calendarSharedBranchId?: string | null
  tenantFeatures?: { tiperCanAssignBroker: boolean }
  aiConsentAt: string | null
  /** Prepínač AI asistenta v Môj tím → Nastavenia (chýba = staršie API → zapnutý). */
  assistantEnabled?: boolean
  /** Zostatok AI kreditov — `/auth/me` ho vracia, login odpoveď nie (preto voliteľné). */
  credits?: number
}

export interface TwoFactorChallenge {
  twoFactorRequired: true
  challenge: string
  methods: Array<'TOTP' | 'EMAIL'>
  defaultMethod: 'TOTP' | 'EMAIL'
}

export interface LoginSuccess {
  accessToken: string
  user: AuthUser
  mustChangePassword?: boolean
}

export type LoginResult = LoginSuccess | TwoFactorChallenge

function persistSession(data: LoginSuccess) {
  localStorage.setItem('accessToken', data.accessToken)
  if (data.mustChangePassword) {
    localStorage.setItem('mustChangePassword', '1')
  }
  // Clear tenant override on login — SUPER_ADMIN will re-select via sidebar
  localStorage.removeItem('superadmin_tenant_id')
}

export async function login(email: string, password: string): Promise<LoginResult> {
  // Chyby prihlásenia ukazuje LoginPage inline (skipErrorToast).
  const { data } = await api.post<LoginResult>('/auth/login', { email, password }, { skipErrorToast: true })
  if ('twoFactorRequired' in data) return data
  persistSession(data)
  return data
}

export async function sendLoginEmailCode(challenge: string): Promise<void> {
  await api.post('/auth/login/send-email-code', { challenge }, { skipErrorToast: true })
}

export async function loginVerify(
  challenge: string,
  method: 'TOTP' | 'EMAIL' | 'RECOVERY',
  code: string,
  trustDevice: boolean,
): Promise<LoginSuccess> {
  const { data } = await api.post<LoginSuccess>(
    '/auth/login/verify',
    { challenge, method, code, trustDevice },
    { skipErrorToast: true },
  )
  persistSession(data)
  return data
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout').catch(() => {})
  localStorage.removeItem('accessToken')
  // Ďalší používateľ na tom istom prehliadači nesmie dostať obnovu cudzieho chatu asistenta.
  try { localStorage.removeItem(ASSISTANT_CHAT_ID_KEY) } catch { /* zablokované úložisko */ }
}

export async function getMe(): Promise<AuthUser> {
  // Overenie session pri štarte a obnova usera — volajúci chybu riešia sami (AuthContext → /login).
  const { data } = await api.get<AuthUser>('/auth/me', { skipErrorToast: true })
  return data
}

export function isLoggedIn(): boolean {
  return !!localStorage.getItem('accessToken')
}
